// DataTable — search + sort + column-toggle + pagination + export (Excel/PDF/print) + bulk select.
// Uses tabular-nums, virtualization triggers via `virtualize` prop for large datasets.
import { useMemo, useState, useEffect, useRef } from "react";
import { Search, Download, Printer, ArrowUp, ArrowDown, Columns3, FileText, FileSpreadsheet } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuCheckboxItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { exportExcel, exportPDF, printData } from "@/utils/export";
import { useT } from "@/i18n";

export function DataTable({
  data = [], columns = [], title = "Kayıt Listesi",
  loading = false, testId = "data-table",
  selectable = false, onBulkAction, rowActions,
  initialSort, defaultPageSize = 25,
  pdfSummary,  // opsiyonel: PDF'in üstüne konacak özet [{label, value, color?, subtitle?}]
  bulkExtras,  // opsiyonel: (selectedIds, clearSelection) => ReactNode — çoklu seçildiğinde gösterilecek ek butonlar
}) {
  const { t } = useT();
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState(initialSort?.key || null);
  const [sortDir, setSortDir] = useState(initialSort?.dir || "asc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [selected, setSelected] = useState(new Set());
  // Kolon imzası — kolonlar değişince hidden state otomatik yeniden hesaplanır.
  const colSignature = columns.map(c => c.key).join("|");
  const defaultHidden = useMemo(
    () => new Set(columns.filter(c => c.hiddenByDefault).map(c => c.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [colSignature]
  );
  const [hidden, setHidden] = useState(defaultHidden);
  const prevSigRef = useRef(colSignature);
  useEffect(() => {
    if (prevSigRef.current !== colSignature) {
      prevSigRef.current = colSignature;
      setHidden(defaultHidden);
    }
  }, [colSignature, defaultHidden]);

  const visibleColumns = columns.filter(c => !hidden.has(c.key));

  const filtered = useMemo(() => {
    if (!query) return data;
    const q = query.toLowerCase();
    return data.filter((row) =>
      columns.some((c) => {
        const v = c.accessor ? c.accessor(row) : row[c.key];
        return String(v ?? "").toLowerCase().includes(q);
      })
    );
  }, [data, columns, query]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const col = columns.find(c => c.key === sortKey);
    if (!col) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    // ISO tarih regex — YYYY-MM-DD ile başlayan
    const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;
    return [...filtered].sort((a, b) => {
      const va = col.sortAccessor ? col.sortAccessor(a) :
                 col.accessor ? col.accessor(a) : a[sortKey];
      const vb = col.sortAccessor ? col.sortAccessor(b) :
                 col.accessor ? col.accessor(b) : b[sortKey];
      // null/undefined en sona
      const aNull = va === null || va === undefined || va === "";
      const bNull = vb === null || vb === undefined || vb === "";
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      // ISO tarih: string olarak karşılaştır (YYYY-MM-DD doğal sıralanır)
      const sa = String(va), sb = String(vb);
      if (ISO_DATE.test(sa) && ISO_DATE.test(sb)) {
        return sa.localeCompare(sb) * dir;
      }
      // Sayı mı? SADECE tamamen numeric string ise parseFloat kullan
      // (Aksi halde "2026-03-15" -> 2026 olarak yanlış parse edilir)
      const NUMERIC = /^-?\d+(\.\d+)?$/;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      if (NUMERIC.test(sa.trim()) && NUMERIC.test(sb.trim())) {
        return (parseFloat(sa) - parseFloat(sb)) * dir;
      }
      return sa.localeCompare(sb, "tr") * dir;
    });
  }, [filtered, sortKey, sortDir, columns]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageRows = sorted.slice(page * pageSize, page * pageSize + pageSize);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const toggleAllOnPage = () => {
    const ids = pageRows.map(r => r.id);
    const allSel = ids.every(id => selected.has(id));
    const next = new Set(selected);
    ids.forEach(id => allSel ? next.delete(id) : next.add(id));
    setSelected(next);
  };

  const exportCols = visibleColumns.filter(c => !c.excludeFromExport);
  const exportRows = sorted.map(r => {
    const o = {};
    exportCols.forEach(c => {
      o[c.key] = c.exportAccessor ? c.exportAccessor(r) :
                 c.accessor ? String(c.accessor(r) ?? "") : String(r[c.key] ?? "");
    });
    return o;
  });
  const exportColsSimple = exportCols.map(c => ({ key: c.key, header: c.header, width: c.width || 18 }));

  return (
    <div className="surface" data-testid={testId}>
      {/* Toolbar */}
      <div className="p-4 border-b border-border flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("common.search") + "..."}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(0); }}
              className="pl-9 h-9"
              data-testid={`${testId}-search`}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectable && selected.size > 0 && (
            <>
              <span className="text-sm text-muted-foreground">{selected.size} {t("common.selected")}</span>
              {bulkExtras && bulkExtras(
                [...selected],
                data.filter(r => selected.has(r.id)),
                () => setSelected(new Set()),
              )}
              {onBulkAction && (
                <Button variant="outline" size="sm" onClick={() => { onBulkAction([...selected]); setSelected(new Set()); }} data-testid={`${testId}-bulk-btn`}>
                  Toplu Sil
                </Button>
              )}
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid={`${testId}-cols-btn`}>
                <Columns3 className="h-4 w-4 mr-1" /> {t("common.showColumns")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {columns.map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.key}
                  checked={!hidden.has(c.key)}
                  onCheckedChange={(ck) => {
                    const next = new Set(hidden);
                    ck ? next.delete(c.key) : next.add(c.key);
                    setHidden(next);
                  }}
                >
                  {c.header}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid={`${testId}-export-btn`}>
                <Download className="h-4 w-4 mr-1" /> {t("common.export")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t("common.export")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => exportExcel(title, exportRows, exportColsSimple)} data-testid={`${testId}-export-excel`}>
                <FileSpreadsheet className="h-4 w-4 mr-2" /> {t("common.excel")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF(title, exportRows, exportColsSimple, { summary: pdfSummary })} data-testid={`${testId}-export-pdf`}>
                <FileText className="h-4 w-4 mr-2" /> {t("common.pdf")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => printData(title, exportRows, exportColsSimple)} data-testid={`${testId}-export-print`}>
                <Printer className="h-4 w-4 mr-2" /> {t("common.print")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Table — mobil/Safari için yatay kaydırma + momentum scroll */}
      <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              {selectable && (
                <th className="w-10 px-3 py-2.5">
                  <Checkbox
                    checked={pageRows.length > 0 && pageRows.every(r => selected.has(r.id))}
                    onCheckedChange={toggleAllOnPage}
                    aria-label="Tümünü seç"
                  />
                </th>
              )}
              {visibleColumns.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-2.5 label-caps whitespace-nowrap ${c.align === "right" ? "text-right" : "text-left"} ${c.sortable !== false ? "cursor-pointer select-none hover:text-foreground" : ""}`}
                  onClick={() => c.sortable !== false && toggleSort(c.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.header}
                    {sortKey === c.key && (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                  </span>
                </th>
              ))}
              {rowActions && <th className="w-10 px-3 py-2.5" />}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={visibleColumns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)} className="p-8 text-center text-muted-foreground">{t("common.loading")}</td></tr>
            )}
            {!loading && pageRows.length === 0 && (
              <tr><td colSpan={visibleColumns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)} className="p-8 text-center text-muted-foreground">{t("common.empty")}</td></tr>
            )}
            {!loading && pageRows.map((r, i) => (
              <tr key={r.id || i} className="border-b border-border hover:bg-muted/30 transition-colors" data-testid={`${testId}-row-${i}`}>
                {selectable && (
                  <td className="px-3 py-2.5">
                    <Checkbox
                      checked={selected.has(r.id)}
                      onCheckedChange={() => {
                        const next = new Set(selected);
                        next.has(r.id) ? next.delete(r.id) : next.add(r.id);
                        setSelected(next);
                      }}
                    />
                  </td>
                )}
                {visibleColumns.map((c) => (
                  <td key={c.key} className={`px-3 py-2.5 ${c.align === "right" ? "text-right tabular-nums" : ""} ${c.className || ""}`}>
                    {c.render ? c.render(r) : (c.accessor ? c.accessor(r) : r[c.key])}
                  </td>
                ))}
                {rowActions && <td className="px-3 py-2.5 text-right">{rowActions(r)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
        <div>
          {sorted.length > 0 ? (
            <>{page * pageSize + 1}–{Math.min((page + 1) * pageSize, sorted.length)} / {sorted.length}</>
          ) : "0 / 0"}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2">
            <span>{t("common.perPage")}:</span>
            <select
              className="border border-border bg-background rounded px-2 py-1 text-xs"
              value={pageSize}
              onChange={(e) => { setPageSize(+e.target.value); setPage(0); }}
              data-testid={`${testId}-page-size`}
            >
              {[10, 25, 50, 100, 250, 500].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid={`${testId}-prev`}>{t("common.previous")}</Button>
            <span className="tabular px-2">{page + 1} / {pageCount}</span>
            <Button variant="ghost" size="sm" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)} data-testid={`${testId}-next`}>{t("common.next")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
